import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import ReactMarkdown from 'react-markdown'
import { get, patch, post } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import './Escalations.scss'

type EscalationStatus = 'pending' | 'in_progress' | 'resolved' | 'cancelled'
type Priority = 'low' | 'medium' | 'high' | 'urgent'
type ResolutionChannel = 'email' | 'whatsapp' | 'phone' | 'in_app' | 'other'

interface AdminNote {
  _id: string
  note: string
  addedBy: { _id: string; firstname: string; lastname: string }
  addedAt: string
}

interface EscalationDetail {
  _id: string
  referenceId: string
  status: EscalationStatus
  priority: Priority
  reason: string
  userId: {
    _id: string
    firstname: string
    lastname: string
    email: string
    mobile: string
  } | null
  userSnapshot: {
    firstname: string
    lastname: string
    email: string
    phone: string
    role: string
  }
  sessionId: string
  context: {
    lastIntent: string
    pendingAction: string | null
    destination: string
    trackingId: string
    messageCount: number
  }
  recentMessages: {
    role: 'user' | 'assistant'
    content: string
    timestamp: string
  }[]
  assignedTo: {
    _id: string
    firstname: string
    lastname: string
    email: string
  } | null
  resolution: {
    notes: string
    resolvedAt: string
    resolvedBy: { firstname: string; lastname: string }
  } | null
  resolutionChannel: ResolutionChannel | null
  adminNotes: AdminNote[]
  emailNotificationSent: boolean
  createdAt: string
  updatedAt: string
}

const PRIORITY_LABELS: Record<Priority, string> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

const STATUS_ICONS: Record<EscalationStatus, string> = {
  pending: '⏳',
  in_progress: '🔄',
  resolved: '✅',
  cancelled: '❌',
}

const STATUS_LABELS: Record<EscalationStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
}

const CHANNELS: { value: ResolutionChannel; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'phone', label: 'Phone' },
  { value: 'in_app', label: 'In-App' },
  { value: 'other', label: 'Other' },
]

function PriorityBadge({ priority }: { priority: Priority }) {
  return (
    <span className={`badge priority-${priority}`}>
      {PRIORITY_LABELS[priority]}
    </span>
  )
}

function StatusBadge({ status }: { status: EscalationStatus }) {
  return (
    <span className={`badge status-${status}`}>
      {STATUS_ICONS[status]} {STATUS_LABELS[status]}
    </span>
  )
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-NG', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function ResolveModal({
  referenceId,
  onConfirm,
  onCancel,
  loading,
}: {
  referenceId: string
  onConfirm: (notes: string, channel: ResolutionChannel) => void
  onCancel: () => void
  loading: boolean
}) {
  const [notes, setNotes] = useState('')
  const [channel, setChannel] = useState<ResolutionChannel | ''>('')

  return (
    <div className='resolve-modal-overlay'>
      <div className='resolve-modal'>
        <h3>Resolve Escalation {referenceId}</h3>
        <label>Resolution Notes *</label>
        <textarea
          placeholder='Describe how this was resolved…'
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <label>Channel Used *</label>
        <div className='channel-options'>
          {CHANNELS.map((ch) => (
            <label
              key={ch.value}
              className={channel === ch.value ? 'selected' : ''}
            >
              <input
                type='radio'
                value={ch.value}
                checked={channel === ch.value}
                onChange={() => setChannel(ch.value)}
              />
              {ch.label}
            </label>
          ))}
        </div>
        <div className='modal-actions'>
          <button className='btn-cancel' onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button
            className='btn-confirm'
            onClick={() => {
              if (notes.trim() && channel)
                onConfirm(notes.trim(), channel as ResolutionChannel)
            }}
            disabled={loading || !notes.trim() || !channel}
          >
            {loading ? 'Resolving…' : 'Mark Resolved'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Escalation() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { notify } = useGlobalContext()

  const [escalation, setEscalation] = useState<EscalationDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [showResolveModal, setShowResolveModal] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [noteLoading, setNoteLoading] = useState(false)

  function fetchEscalation() {
    setLoading(true)
    get(`admin/escalations/${id}`)
      .then((res: any) => {
        if (res.success) {
          setEscalation(res.data)
        } else {
          notify('error', res.message || 'Failed to load escalation')
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to load escalation')
      })
  }

  useEffect(() => {
    if (id) fetchEscalation()
  }, [id])

  const assignToMe = async () => {
    if (!escalation) return
    setActionLoading(true)
    try {
      const res = await patch(`admin/escalations/${escalation._id}/assign`, {})
      if (res.success) {
        notify('success', 'Assigned to you')
        setEscalation(res.data)
      } else {
        notify('error', res.message || 'Failed to assign')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
    setActionLoading(false)
  }

  const startProgress = async () => {
    if (!escalation) return
    setActionLoading(true)
    try {
      const res = await patch(`admin/escalations/${escalation._id}/status`, {
        status: 'in_progress',
      })
      if (res.success) {
        notify('success', 'Marked as In Progress')
        setEscalation(res.data)
      } else {
        notify('error', res.message || 'Failed to update status')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
    setActionLoading(false)
  }

  const cancelEscalation = async () => {
    if (!escalation) return
    if (!window.confirm('Are you sure you want to cancel this escalation?'))
      return
    setActionLoading(true)
    try {
      const res = await patch(`admin/escalations/${escalation._id}/status`, {
        status: 'cancelled',
      })
      if (res.success) {
        notify('success', 'Escalation cancelled')
        setEscalation(res.data)
      } else {
        notify('error', res.message || 'Failed to cancel')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
    setActionLoading(false)
  }

  const handleResolve = async (notes: string, channel: ResolutionChannel) => {
    if (!escalation) return
    setActionLoading(true)
    try {
      const res = await patch(`admin/escalations/${escalation._id}/status`, {
        status: 'resolved',
        resolutionNotes: notes,
        resolutionChannel: channel,
      })
      if (res.success) {
        notify('success', 'Escalation resolved')
        setEscalation(res.data)
        setShowResolveModal(false)
      } else {
        notify('error', res.message || 'Failed to resolve')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
    setActionLoading(false)
  }

  const addNote = async () => {
    if (!escalation || !noteText.trim()) return
    setNoteLoading(true)
    try {
      const res = await post(
        `admin/escalations/${escalation._id}/notes`,
        { note: noteText.trim() },
        null,
      )
      if (res.success) {
        notify('success', 'Note added')
        setEscalation(res.data)
        setNoteText('')
      } else {
        notify('error', res.message || 'Failed to add note')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
    setNoteLoading(false)
  }

  if (loading) {
    return (
      <div className='escalation-detail'>
        <FormLoader position='center' />
      </div>
    )
  }

  if (!escalation) {
    return (
      <div className='escalation-detail'>
        <div
          style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#888',
          }}
        >
          <Icon
            icon='mdi:alert-circle-outline'
            style={{ fontSize: 48, marginBottom: 12 }}
          />
          <p>Escalation not found</p>
          <button
            onClick={() => navigate('/admin/escalations')}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              background: '#446ce4',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Back to Escalations
          </button>
        </div>
      </div>
    )
  }

  const userName = escalation.userId
    ? `${escalation.userId.firstname} ${escalation.userId.lastname}`
    : `${escalation.userSnapshot.firstname} ${escalation.userSnapshot.lastname}`
  const userEmail = escalation.userId?.email || escalation.userSnapshot.email
  const userPhone = escalation.userId?.mobile || escalation.userSnapshot.phone

  const isResolved = escalation.status === 'resolved'
  const isCancelled = escalation.status === 'cancelled'
  const isDone = isResolved || isCancelled

  return (
    <div className='escalation-detail'>
      {/* Back row */}
      <div className='back-row' onClick={() => navigate('/admin/escalations')}>
        <Icon icon='mdi:arrow-left' />
        <span>Back to Escalations</span>
      </div>

      {/* Header */}
      <div className='detail-header'>
        <h2>{escalation.referenceId}</h2>
        <PriorityBadge priority={escalation.priority} />
        <StatusBadge status={escalation.status} />
      </div>

      {/* Grid */}
      <div className='detail-grid'>
        {/* Left column */}
        <div className='left-col'>
          {/* User info */}
          <div className='detail-card'>
            <div className='card-title'>User Info</div>
            <div className='card-body'>
              <p>
                <strong>Name:</strong>{' '}
                <span
                  style={{ color: '#446ce4', cursor: 'pointer' }}
                  onClick={() =>
                    escalation.userId &&
                    navigate(`/admin/customer/${escalation.userId._id}`)
                  }
                >
                  {userName}
                </span>
              </p>
              <p>
                <strong>Email:</strong> {userEmail}
              </p>
              <p>
                <strong>Phone:</strong> {userPhone || '—'}
              </p>
              <p>
                <strong>Role:</strong> {escalation.userSnapshot.role}
              </p>
            </div>
          </div>

          {/* Context */}
          <div className='detail-card'>
            <div className='card-title'>Chat Context</div>
            <div className='card-body'>
              <p>
                <strong>Last Topic:</strong>{' '}
                {escalation.context.lastIntent || '—'}
              </p>
              {escalation.context.trackingId && (
                <p>
                  <strong>Tracking ID:</strong> {escalation.context.trackingId}
                </p>
              )}
              {escalation.context.destination && (
                <p>
                  <strong>Destination:</strong> {escalation.context.destination}
                </p>
              )}
              <p>
                <strong>Messages in Session:</strong>{' '}
                {escalation.context.messageCount}
              </p>
              {escalation.reason && (
                <p>
                  <strong>Reason Provided:</strong> "{escalation.reason}"
                </p>
              )}
            </div>
          </div>

          {/* Chat transcript */}
          <div className='detail-card'>
            <div className='card-title'>Chat Transcript</div>
            <div className='card-body'>
              {escalation.recentMessages.length === 0 ? (
                <p style={{ color: '#aaa', fontStyle: 'italic' }}>
                  No messages recorded
                </p>
              ) : (
                <div className='chat-transcript'>
                  {escalation.recentMessages.map((msg, i) => (
                    <div key={i} className={`chat-message ${msg.role}`}>
                      <div className='bubble-wrap'>
                        <span className='sender-label'>
                          {msg.role === 'user' ? 'User' : 'QC Buddy'}
                        </span>
                        <div className='bubble'>
                          {msg.role === 'assistant' ? (
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          ) : (
                            <p>{msg.content}</p>
                          )}
                          <p className='timestamp'>
                            {formatDateTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Resolution info (if resolved) */}
          {isResolved && escalation.resolution && (
            <div className='detail-card'>
              <div className='card-title'>Resolution</div>
              <div className='card-body'>
                <p>
                  <strong>Notes:</strong> {escalation.resolution.notes}
                </p>
                <p>
                  <strong>Channel:</strong>{' '}
                  {CHANNELS.find(
                    (c) => c.value === escalation.resolutionChannel,
                  )?.label || escalation.resolutionChannel}
                </p>
                <p>
                  <strong>Resolved by:</strong>{' '}
                  {escalation.resolution.resolvedBy.firstname}{' '}
                  {escalation.resolution.resolvedBy.lastname}
                </p>
                <p>
                  <strong>Resolved at:</strong>{' '}
                  {formatDateTime(escalation.resolution.resolvedAt)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div className='right-col'>
          {/* Actions */}
          <div className='detail-card'>
            <div className='card-title'>Actions</div>
            <div className='card-body'>
              <div className='action-buttons'>
                {!escalation.assignedTo && !isDone && (
                  <button
                    className='btn-primary'
                    onClick={assignToMe}
                    disabled={actionLoading}
                  >
                    <Icon icon='mdi:account-check' />
                    Assign to Me
                  </button>
                )}
                {escalation.status === 'pending' && (
                  <button
                    className='btn-warning'
                    onClick={startProgress}
                    disabled={actionLoading}
                  >
                    <Icon icon='mdi:play-circle-outline' />
                    Start Progress
                  </button>
                )}
                {escalation.status !== 'resolved' && !isCancelled && (
                  <button
                    className='btn-success'
                    onClick={() => setShowResolveModal(true)}
                    disabled={actionLoading}
                  >
                    <Icon icon='mdi:check-circle-outline' />
                    Resolve
                  </button>
                )}
                {!isDone && (
                  <button
                    className='btn-danger'
                    onClick={cancelEscalation}
                    disabled={actionLoading}
                  >
                    <Icon icon='mdi:close-circle-outline' />
                    Cancel Escalation
                  </button>
                )}
              </div>

              {/* Assigned to */}
              <div style={{ marginTop: '14px' }}>
                <span
                  className={`assigned-chip ${!escalation.assignedTo ? 'unassigned' : ''}`}
                >
                  <Icon
                    icon={
                      escalation.assignedTo ? 'mdi:account' : 'mdi:account-off'
                    }
                  />
                  {escalation.assignedTo
                    ? `${escalation.assignedTo.email} `
                    : 'Unassigned'}
                </span>
              </div>

              <p style={{ fontSize: '12px', color: '#aaa', marginTop: '10px' }}>
                Created: {formatDateTime(escalation.createdAt)}
              </p>
              <p style={{ fontSize: '12px', color: '#aaa' }}>
                Updated: {formatDateTime(escalation.updatedAt)}
              </p>
            </div>
          </div>

          {/* Internal notes */}
          <div className='detail-card'>
            <div className='card-title'>Internal Notes</div>
            <div className='card-body'>
              {escalation.adminNotes.length === 0 ? (
                <p
                  style={{
                    color: '#aaa',
                    fontStyle: 'italic',
                    fontSize: '13px',
                  }}
                >
                  No notes yet
                </p>
              ) : (
                escalation.adminNotes.map((note) => (
                  <div key={note._id} className='note-item'>
                    <p>{note.note}</p>
                    <p className='note-meta'>
                      {note.addedBy.firstname} {note.addedBy.lastname} ·{' '}
                      {formatDateTime(note.addedAt)}
                    </p>
                  </div>
                ))
              )}

              {!isDone && (
                <div className='note-input-wrap'>
                  <textarea
                    placeholder='Add an internal note…'
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                  />
                  <button
                    onClick={addNote}
                    disabled={noteLoading || !noteText.trim()}
                  >
                    {noteLoading ? 'Adding…' : 'Add Note'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showResolveModal && (
        <ResolveModal
          referenceId={escalation.referenceId}
          onConfirm={handleResolve}
          onCancel={() => setShowResolveModal(false)}
          loading={actionLoading}
        />
      )}
    </div>
  )
}

export default Escalation
